/**
 * Date Utilities Module
 *
 * This module provides date utilities using native JavaScript Date API.
 * Replaces moment.js dependency with standard browser APIs.
 */

// Store current locale
let currentLocale = 'es';

/**
 * Initialize the locale for date formatting
 * @param {string} locale - The locale to set (e.g., 'es', 'en')
 */
export const initializeLocale = (locale = 'es') => {
  currentLocale = locale;
}

/**
 * Set the locale for date formatting
 * @param {string} locale - The locale to set
 */
export const setLocale = (locale) => {
  currentLocale = locale;
}

/**
 * Get the current locale
 * @returns {string} The current locale
 */
export const getLocale = () => {
  return currentLocale;
}

// Helper to normalize input to Date object
const toDateObj = (date) => {
  if (date instanceof Date) return date;
  if (typeof date === 'number') {
    // If it's a large number (milliseconds), use as-is, otherwise assume Unix seconds
    if (date > 1000000000000) return new Date(date);
    return new Date(date * 1000); // Assume Unix timestamp in seconds
  }
  if (typeof date === 'string') {
    // Handle YYYY-MM-DD format specially to avoid timezone issues
    const dateOnlyMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    }
    // For other strings, use standard parsing
    return new Date(date);
  }
  return new Date(date);
}

/**
 * Parse a date string or object into a Date object
 * @param {string | Date | number} date - The date to parse
 * @returns {Date} A Date object
 */
export const parse = (date) => {
  return toDateObj(date);
}

/**
 * Parse a Unix timestamp into a Date object
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {Date} A Date object
 */
export const parseUnix = (timestamp) => {
  return new Date(timestamp * 1000);
}

/**
 * Parse a date as UTC
 * @param {string | Date} date - The date to parse
 * @returns {Date} A Date object in UTC
 */
export const parseUTC = (date) => {
  const d = toDateObj(date);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
}

/**
 * Parse a date with a specific format (limited support)
 * @param {string} date - The date string to parse
 * @param {string} format - The format of the date string
 * @returns {Date} A Date object
 */
export const parseWithFormat = (date, format) => {
  // Simple format parser for common patterns
  if (format === 'HH:mm:ss') {
    const parts = date.split(':');
    const d = new Date();
    d.setHours(parseInt(parts[0], 10), parseInt(parts[1], 10), parseInt(parts[2], 10));
    return d;
  }
  
  // For DD/MM/YYYY
  if (format === 'DD/MM/YYYY') {
    const parts = date.split('/');
    return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
  }
  
  // Default: try standard parsing
  return new Date(date);
}

/**
 * Get the current date/time
 * @returns {Date} A Date object representing now
 */
export const now = () => {
  return new Date();
}

// Spanish month names (lowercase) - used by default for human-readable dates
const spanishMonths = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

// Spanish month names (capitalized)
const spanishMonthsCapitalized = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// English month names (lowercase)
const englishMonths = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
];

// English month names (capitalized)
const englishMonthsCapitalized = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Get month names based on current locale
 * @param {boolean} capitalized - Whether to return capitalized names
 * @returns {string[]} Array of month names
 */
const getMonths = (capitalized = false) => {
  if (currentLocale === 'es') {
    return capitalized ? spanishMonthsCapitalized : spanishMonths;
  }
  return capitalized ? englishMonthsCapitalized : englishMonths;
}

/**
 * Format a date
 * @param {string | Date | number} date - The date to format
 * @param {string} formatStr - The format string (e.g., 'YYYY-MM-DD', 'LL')
 * @returns {string} The formatted date string
 */
export const format = (date, formatStr) => {
  const d = toDateObj(date);
  
  // Handle special moment formats
  if (formatStr === 'LL') {
    // e.g., "20 de febrero de 2019" in Spanish
    if (currentLocale === 'es') {
      return `${d.getDate()} de ${spanishMonths[d.getMonth()]} de ${d.getFullYear()}`;
    }
    return d.toLocaleDateString(currentLocale, { year: 'numeric', month: 'long', day: 'numeric' });
  }
  
  if (formatStr === 'LLLL') {
    // Full localized date with time
    return d.toLocaleString(currentLocale, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  if (formatStr === 'X') {
    return Math.floor(d.getTime() / 1000).toString();
  }
  
  // Handle custom format patterns
  const months = getMonths(false); // Use lowercase by default for human-readable dates
  
  const year = d.getFullYear();
  const replacements = {
    'YYYY': year,
    'YY': String(year).slice(-2),
    'MMMM': months[d.getMonth()],
    'MMM': months[d.getMonth()].substring(0, 3),
    'MM': String(d.getMonth() + 1).padStart(2, '0'),
    'DD': String(d.getDate()).padStart(2, '0'),
    'HH': String(d.getHours()).padStart(2, '0'),
    'mm': String(d.getMinutes()).padStart(2, '0'),
    'ss': String(d.getSeconds()).padStart(2, '0')
  };
  
  let result = formatStr;
  // Sort patterns by length (longest first) to avoid partial replacements
  const sortedPatterns = Object.entries(replacements).sort((a, b) => b[0].length - a[0].length);
  for (const [pattern, value] of sortedPatterns) {
    // Use a regex with global flag for replacement
    const regex = new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    result = result.replace(regex, String(value));
  }
  
  return result;
}

/**
 * Add time to a date
 * @param {string | Date | number} date - The date to manipulate
 * @param {number} amount - The amount to add
 * @param {string} unit - The unit (e.g., 'days', 'months', 'years', 'seconds')
 * @returns {Date} A Date object
 */
export const add = (date, amount, unit) => {
  const d = new Date(toDateObj(date));
  
  switch(unit) {
    case 'days':
      d.setDate(d.getDate() + amount);
      break;
    case 'months':
      d.setMonth(d.getMonth() + amount);
      break;
    case 'years':
      d.setFullYear(d.getFullYear() + amount);
      break;
    case 'hours':
      d.setHours(d.getHours() + amount);
      break;
    case 'minutes':
      d.setMinutes(d.getMinutes() + amount);
      break;
    case 'seconds':
      d.setSeconds(d.getSeconds() + amount);
      break;
    default:
      // Do nothing for unknown units
      break;
  }
  
  return d;
}

/**
 * Check if a date is after another date
 * @param {string | Date | number} date - The date to check
 * @param {string | Date | number} comparison - The date to compare against
 * @returns {boolean} True if date is after comparison
 */
export const isAfter = (date, comparison) => {
  return toDateObj(date) > toDateObj(comparison);
}

/**
 * Check if a date is before another date
 * @param {string | Date | number} date - The date to check
 * @param {string | Date | number} comparison - The date to compare against
 * @returns {boolean} True if date is before comparison
 */
export const isBefore = (date, comparison) => {
  return toDateObj(date) < toDateObj(comparison);
}

/**
 * Check if a date is the same as or after another date
 * @param {string | Date | number} date - The date to check
 * @param {string | Date | number} comparison - The date to compare against
 * @returns {boolean} True if date is same or after comparison
 */
export const isSameOrAfter = (date, comparison) => {
  return toDateObj(date) >= toDateObj(comparison);
}

/**
 * Calculate the difference between two dates
 * @param {string | Date | number} date - The date to check
 * @param {string | Date | number} comparison - The date to compare against
 * @param {string} unit - The unit of measurement (e.g., 'minutes', 'days', 'months')
 * @returns {number} The difference
 */
export const diff = (date, comparison, unit) => {
  const d1 = toDateObj(date);
  const d2 = toDateObj(comparison);
  const diffMs = d1.getTime() - d2.getTime();
  
  switch(unit) {
    case 'days':
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    case 'hours':
      return Math.floor(diffMs / (1000 * 60 * 60));
    case 'minutes':
      return Math.floor(diffMs / (1000 * 60));
    case 'seconds':
      return Math.floor(diffMs / 1000);
    case 'months':
      return (d1.getFullYear() - d2.getFullYear()) * 12 + (d1.getMonth() - d2.getMonth());
    case 'years':
      return d1.getFullYear() - d2.getFullYear();
    default:
      return diffMs;
  }
}

/**
 * Convert a date to a specific timezone
 * @param {string | Date | number} date - The date to convert
 * @param {string} timezone - The timezone to convert to (e.g., 'America/Mexico_City')
 * @returns {Date} A Date object in the specified timezone
 */
export const toTimeZone = (date, timezone) => {
  // For native Date, we just return the date object
  // Timezone conversion would require additional libraries or manual offset calculation
  // This is a simplified implementation that maintains API compatibility
  return toDateObj(date);
}

/**
 * Convert a date to a human-readable format
 * @param {string | Date | null | undefined} date - The date to format
 * @param {string} formatStr - The format string (default: 'DD-MMMM-YYYY')
 * @returns {string} The formatted date string or '-' if date is null/undefined
 */
export const toHumanDate = (date, formatStr = 'DD-MMMM-YYYY') => {
  if (!date) return '-'
  return format(date, formatStr);
}

/**
 * Sort dates in ascending order (for use in Array.sort)
 * @param {string | Date | number} a - First date
 * @param {string | Date | number} b - Second date
 * @returns {number} -1 if a < b, 1 if a > b, 0 if equal
 */
export const sortAsc = (a, b) => {
  const d1 = toDateObj(a);
  const d2 = toDateObj(b);
  if (d1 < d2) return -1;
  if (d1 > d2) return 1;
  return 0;
}

/**
 * Sort dates in descending order (for use in Array.sort)
 * @param {string | Date | number} a - First date
 * @param {string | Date | number} b - Second date
 * @returns {number} -1 if a > b, 1 if a < b, 0 if equal
 */
export const sortDesc = (a, b) => {
  const d1 = toDateObj(a);
  const d2 = toDateObj(b);
  if (d1 > d2) return -1;
  if (d1 < d2) return 1;
  return 0;
}

/**
 * Get Unix timestamp from a date
 * @param {string | Date | number} date - The date
 * @returns {string} Unix timestamp in seconds
 */
export const toUnix = (date) => {
  return Math.floor(toDateObj(date).getTime() / 1000).toString();
}

/**
 * Convert a date to a JavaScript Date object
 * @param {string | Date | number} date - The date
 * @returns {Date} A JavaScript Date object
 */
export const toDate = (date) => {
  return toDateObj(date);
}

/**
 * Convert a Unix timestamp to a JavaScript Date object
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {Date} A JavaScript Date object
 */
export const unixToDate = (timestamp) => {
  return new Date(timestamp * 1000);
}

// Default export
const dateUtils = {
  initializeLocale,
  setLocale,
  getLocale,
  parse,
  parseUnix,
  parseUTC,
  parseWithFormat,
  now,
  format,
  add,
  isAfter,
  isBefore,
  isSameOrAfter,
  diff,
  toTimeZone,
  toHumanDate,
  sortAsc,
  sortDesc,
  toUnix,
  toDate,
  unixToDate
};

export default dateUtils;
